import Phaser from 'phaser';
import {
  calculateEmergencyBagScore,
  EMERGENCY_BAG_MAX_SELECT,
  EmergencyBagItem,
  getEmergencyBagProgress,
  getEmergencyItemById,
  isIdealEmergencyBagSelection,
  shuffleEmergencyBagItems,
  validateEmergencyBagSelection,
} from '../data/emergencyBagItems';
import { FullscreenSystem } from '../systems/FullscreenSystem';
import { OverlayInputLock } from '../systems/OverlayInputLock';

const OVERLAY_ID = 'emergency-bag-overlay';
const SCROLL_TAP_SLOP = 20;
const TOGGLE_GUARD_MS = 80;

const DISTRACTOR_FEEDBACK =
  'Barang ini tidak mendesak. Membawa terlalu banyak beban bisa memperlambat evakuasimu.';
const BAG_FULL_FEEDBACK =
  'Tas sudah berisi 10 barang. Ketuk barang terpilih untuk melepas, lalu pilih penggantinya.';

const PANEL_HTML = `
  <div class="eb-panel">
    <div class="eb-head">
      <h2 class="eb-title">Tas Siaga Bencana</h2>
      <p class="eb-subtitle">Pilih 10 barang wajib BNPB.</p>
      <p class="eb-counter"><span id="eb-counter">0/10</span> item dipilih</p>
      <p class="eb-feedback" id="eb-feedback" aria-live="polite"></p>
    </div>
    <div class="eb-list" id="eb-list" role="group" aria-label="Daftar barang tas siaga"></div>
    <div class="eb-summary" id="eb-summary" hidden></div>
    <button type="button" class="eb-done-btn" id="eb-done-btn" disabled>Selesai</button>
  </div>
`;

export interface EmergencyBagResult {
  selectedIds: string[];
  totalPoints: number;
  goodItems: EmergencyBagItem[];
  badItems: EmergencyBagItem[];
}

export interface EmergencyBagOverlayConfig {
  onComplete?: (result: EmergencyBagResult) => void;
  onVisibility?: (visible: boolean) => void;
}

let overlaySingleton: EmergencyBagOverlay | null = null;
let activeBagOverlay: EmergencyBagOverlay | null = null;

/** Sembunyikan overlay HTML saat restart/transisi zona (singleton DOM). */
export function hideEmergencyBagOverlay(): void {
  if (overlaySingleton) {
    overlaySingleton.hide();
    return;
  }
  OverlayInputLock.releaseAll();
  const el = document.getElementById(OVERLAY_ID);
  if (!el) return;
  el.classList.remove('visible');
  el.style.pointerEvents = 'none';
}

export function createEmergencyBagOverlay(): EmergencyBagOverlay {
  if (overlaySingleton) return overlaySingleton;
  overlaySingleton = new EmergencyBagOverlay();
  return overlaySingleton;
}

export class EmergencyBagOverlay {
  private overlay: HTMLDivElement;
  private panelEl: HTMLDivElement;
  private listEl: HTMLDivElement;
  private counterEl: HTMLSpanElement;
  private feedbackEl: HTMLParagraphElement;
  private doneBtn: HTMLButtonElement;
  private summaryEl: HTMLDivElement;
  private selected = new Set<string>();
  private config: EmergencyBagOverlayConfig = {};
  private finished = false;
  private summaryResult?: EmergencyBagResult;
  private lockedScene?: Phaser.Scene;
  private didPausePhysics = false;
  private layoutListening = false;
  private pointerStartY = 0;
  private pointerStartX = 0;
  private pointerStartId: number | null = null;
  private pointerDragging = false;
  private lastToggleAt = 0;
  private lastToggleId: string | null = null;

  private readonly doneClickHandler = (event: Event): void => {
    event.preventDefault();
    event.stopPropagation();
    if (this.doneBtn.disabled) return;
    if (this.finished) {
      this.finishSummary();
      return;
    }
    this.submit();
  };

  private readonly onLayoutChange = (): void => {
    window.requestAnimationFrame(() => this.fitPanel());
  };

  private readonly onListPointerDown = (event: PointerEvent): void => {
    this.pointerStartY = event.clientY;
    this.pointerStartX = event.clientX;
    this.pointerStartId = event.pointerId;
    this.pointerDragging = false;
  };

  private readonly onListPointerMove = (event: PointerEvent): void => {
    if (this.pointerStartId !== null && event.pointerId !== this.pointerStartId) return;
    if (
      Math.abs(event.clientY - this.pointerStartY) > SCROLL_TAP_SLOP
      || Math.abs(event.clientX - this.pointerStartX) > SCROLL_TAP_SLOP
    ) {
      this.pointerDragging = true;
    }
  };

  private readonly onListPointerUp = (event: PointerEvent): void => {
    if (this.pointerStartId !== null && event.pointerId !== this.pointerStartId) return;
    const wasDragging = this.pointerDragging;
    this.pointerStartId = null;
    this.pointerDragging = false;
    if (wasDragging || this.finished || this.listEl.hidden) return;

    const target = (event.target as HTMLElement | null)?.closest('.eb-item') as HTMLButtonElement | null;
    if (!target || !this.listEl.contains(target)) return;
    const id = target.dataset.id;
    if (id) this.toggleItem(id);
  };

  private readonly onListPointerCancel = (): void => {
    this.pointerStartId = null;
    this.pointerDragging = false;
  };

  private readonly onListKeyClick = (event: MouseEvent): void => {
    if (event.detail !== 0) return;
    const target = (event.target as HTMLElement | null)?.closest('.eb-item') as HTMLButtonElement | null;
    if (!target) return;
    const id = target.dataset.id;
    if (id) this.toggleItem(id);
  };

  constructor() {
    const mount = FullscreenSystem.getOverlayMount();
    let overlay = document.getElementById(OVERLAY_ID) as HTMLDivElement | null;
    if (!overlay) {
      overlay = document.createElement('div');
      overlay.id = OVERLAY_ID;
      mount.appendChild(overlay);
    } else if (overlay.parentElement !== mount) {
      mount.appendChild(overlay);
    }
    overlay.innerHTML = PANEL_HTML;

    overlay.style.pointerEvents = 'none';
    this.overlay = overlay;
    this.panelEl = overlay.querySelector('.eb-panel') as HTMLDivElement;
    this.listEl = overlay.querySelector('#eb-list') as HTMLDivElement;
    this.counterEl = overlay.querySelector('#eb-counter') as HTMLSpanElement;
    this.feedbackEl = overlay.querySelector('#eb-feedback') as HTMLParagraphElement;
    this.doneBtn = overlay.querySelector('#eb-done-btn') as HTMLButtonElement;
    this.summaryEl = overlay.querySelector('#eb-summary') as HTMLDivElement;

    this.bindListEvents();
    this.doneBtn.addEventListener('click', this.doneClickHandler);
  }

  private bindListEvents(): void {
    this.listEl.addEventListener('pointerdown', this.onListPointerDown);
    this.listEl.addEventListener('pointermove', this.onListPointerMove);
    this.listEl.addEventListener('pointerup', this.onListPointerUp);
    this.listEl.addEventListener('pointercancel', this.onListPointerCancel);
    this.listEl.addEventListener('click', this.onListKeyClick);
  }

  private ensureMount(): void {
    const mount = FullscreenSystem.getOverlayMount();
    if (this.overlay.parentElement !== mount) {
      mount.appendChild(this.overlay);
    }
  }

  configure(config: EmergencyBagOverlayConfig): void {
    this.config = config;
  }

  show(scene?: Phaser.Scene): void {
    this.ensureMount();
    this.finished = false;
    this.summaryResult = undefined;
    this.selected.clear();
    this.lastToggleId = null;
    this.pointerStartId = null;
    this.pointerDragging = false;
    this.panelEl.classList.remove('is-summary');
    this.summaryEl.hidden = true;
    this.summaryEl.replaceChildren();
    this.listEl.hidden = false;
    this.listEl.scrollTop = 0;
    this.doneBtn.textContent = 'Selesai';
    this.setFeedback('', null);
    this.buildItemButtons();
    this.updateUI();
    this.overlay.classList.add('visible');
    this.overlay.style.pointerEvents = 'auto';
    this.fitPanel();
    this.bindLayout();

    activeBagOverlay = this;
    if (scene) this.freezeScene(scene);
    this.config.onVisibility?.(true);
  }

  hide(): void {
    const wasOpen = this.overlay.classList.contains('visible') || activeBagOverlay === this;

    this.overlay.classList.remove('visible');
    this.overlay.style.pointerEvents = 'none';
    this.unbindLayout();
    this.unfreezeScene();

    if (activeBagOverlay === this) activeBagOverlay = null;
    if (wasOpen) this.config.onVisibility?.(false);
  }

  isVisible(): boolean {
    return this.overlay.classList.contains('visible');
  }

  private freezeScene(scene: Phaser.Scene): void {
    this.unfreezeScene();
    this.lockedScene = scene;
    OverlayInputLock.acquire(scene);
    try {
      scene.physics.world.pause();
      this.didPausePhysics = true;
    } catch {
      this.didPausePhysics = false;
    }
  }

  private unfreezeScene(): void {
    const scene = this.lockedScene;
    if (!scene) return;
    if (this.didPausePhysics) {
      try {
        scene.physics.world.resume();
      } catch {
        // scene sudah dihancurkan
      }
      this.didPausePhysics = false;
    }
    OverlayInputLock.release(scene);
    this.lockedScene = undefined;
  }

  private bindLayout(): void {
    if (this.layoutListening) return;
    this.layoutListening = true;
    window.addEventListener('resize', this.onLayoutChange);
    window.addEventListener('orientationchange', this.onLayoutChange);
    document.addEventListener('fullscreenchange', this.onLayoutChange);
    document.addEventListener('webkitfullscreenchange', this.onLayoutChange);
    window.visualViewport?.addEventListener('resize', this.onLayoutChange);
  }

  private unbindLayout(): void {
    if (!this.layoutListening) return;
    this.layoutListening = false;
    window.removeEventListener('resize', this.onLayoutChange);
    window.removeEventListener('orientationchange', this.onLayoutChange);
    document.removeEventListener('fullscreenchange', this.onLayoutChange);
    document.removeEventListener('webkitfullscreenchange', this.onLayoutChange);
    window.visualViewport?.removeEventListener('resize', this.onLayoutChange);
  }

  private buildItemButtons(): void {
    const fragment = document.createDocumentFragment();
    shuffleEmergencyBagItems().forEach((item) => {
      const btn = document.createElement('button');
      btn.type = 'button';
      btn.className = 'eb-item';
      btn.dataset.id = item.id;
      btn.textContent = item.name;
      btn.setAttribute('aria-pressed', 'false');
      fragment.appendChild(btn);
    });
    this.listEl.replaceChildren(fragment);
  }

  private toggleItem(id: string): void {
    if (this.finished) return;
    const now = performance.now();
    if (this.lastToggleId === id && now - this.lastToggleAt < TOGGLE_GUARD_MS) return;
    this.lastToggleId = id;
    this.lastToggleAt = now;

    const item = getEmergencyItemById(id);
    if (!item) return;

    if (this.selected.has(id)) {
      this.selected.delete(id);
      this.updateUI(id);
      return;
    }

    if (item.category === 'distractor') {
      this.setFeedback(DISTRACTOR_FEEDBACK, 'err');
      return;
    }

    if (this.selected.size >= EMERGENCY_BAG_MAX_SELECT) {
      this.setFeedback(BAG_FULL_FEEDBACK, 'warn');
      return;
    }

    this.selected.add(id);
    this.updateUI(id);
  }

  private updateUI(changedId?: string): void {
    const selectedIds = [...this.selected];
    const progress = getEmergencyBagProgress(selectedIds);
    const validation = validateEmergencyBagSelection(selectedIds);

    this.counterEl.textContent = `${progress.total}/${EMERGENCY_BAG_MAX_SELECT}`;
    this.counterEl.parentElement?.classList.toggle('is-full', progress.total >= EMERGENCY_BAG_MAX_SELECT);
    this.doneBtn.disabled = !validation.valid;

    if (validation.valid) {
      this.setFeedback(
        isIdealEmergencyBagSelection(selectedIds)
          ? 'Tas siap! Semua barang wajib BNPB lengkap.'
          : '10 barang terpilih. Ketuk Selesai, atau ganti barang jika perlu.',
        'ok',
      );
    } else if (progress.total > 0) {
      this.setFeedback(validation.message, 'warn');
    } else {
      this.setFeedback('', null);
    }

    if (changedId) {
      this.syncItemButton(changedId);
      return;
    }
    this.listEl.querySelectorAll<HTMLButtonElement>('.eb-item').forEach((btn) => {
      this.syncItemButton(btn.dataset.id, btn);
    });
  }

  private syncItemButton(id?: string, btn?: HTMLButtonElement | null): void {
    if (!id) return;
    const el = btn ?? this.listEl.querySelector<HTMLButtonElement>(`.eb-item[data-id="${id}"]`);
    if (!el) return;
    const on = this.selected.has(id);
    el.classList.toggle('selected', on);
    el.setAttribute('aria-pressed', on ? 'true' : 'false');
  }

  private setFeedback(text: string, kind: 'ok' | 'warn' | 'err' | null): void {
    this.feedbackEl.textContent = text;
    this.feedbackEl.classList.remove('is-ok', 'is-warn', 'is-err');
    if (kind) this.feedbackEl.classList.add(`is-${kind}`);
  }

  private submit(): void {
    const selectedIds = [...this.selected];
    const validation = validateEmergencyBagSelection(selectedIds);
    if (!validation.valid || this.finished) {
      this.setFeedback(validation.message, 'err');
      return;
    }
    this.finished = true;
    this.setFeedback('', null);

    const items = selectedIds
      .map((id) => getEmergencyItemById(id))
      .filter((i): i is EmergencyBagItem => !!i);

    this.listEl.hidden = true;
    this.panelEl.classList.add('is-summary');
    this.summaryEl.hidden = false;
    this.summaryEl.replaceChildren();

    const title = document.createElement('h3');
    title.className = 'eb-summary-title';
    title.textContent = 'Ringkasan Tas Darurat';

    const label = document.createElement('p');
    label.className = 'eb-summary-label good';
    label.textContent = `Barang terpilih (${items.length})`;

    const list = document.createElement('ul');
    items.forEach((item) => {
      const li = document.createElement('li');
      li.textContent = item.name;
      list.appendChild(li);
    });

    this.summaryEl.append(title, label, list);
    this.doneBtn.textContent = 'Tutup';
    this.doneBtn.disabled = false;
    this.summaryResult = {
      selectedIds,
      totalPoints: calculateEmergencyBagScore(selectedIds),
      goodItems: items,
      badItems: [],
    };
  }

  private finishSummary(): void {
    if (!this.summaryResult) return;
    const result = this.summaryResult;
    this.summaryResult = undefined;
    this.finished = false;
    this.hide();
    this.doneBtn.textContent = 'Selesai';
    this.config.onComplete?.(result);
  }

  private fitPanel = (): void => {
    if (!this.panelEl) return;
    const vh = window.visualViewport?.height ?? window.innerHeight;
    this.panelEl.style.maxHeight = `${Math.min(Math.round(vh * 0.94), 640)}px`;
  };

  destroy(): void {
    this.hide();
    this.config = {};
  }
}
